import type { Location } from 'react-router-dom';
import type { P1InterruptionDecision } from '@/capabilities/service-operations/api/resolve';
import { workbenchTakeLiveDestination } from '@/app/navigation/routes/workbenchPaths';

const STORAGE_PREFIX = 'hyperion.workbench.p1-interruptions.v1';
const MAX_HANDLED = 32;

export interface P1InterruptionScope {
  tenantId: string;
  actorId: string;
}

interface StoredP1InterruptionState {
  version: 1;
  tenantId: string;
  actorId: string;
  handledItemIds: string[];
}

function storageOrUndefined(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage;
}

function key(scope: P1InterruptionScope): string {
  return `${STORAGE_PREFIX}:${scope.tenantId}:${scope.actorId}`;
}

function readState(
  scope: P1InterruptionScope,
  storage: Storage | undefined = storageOrUndefined()
): StoredP1InterruptionState | null {
  if (!scope.tenantId || !scope.actorId || !storage) return null;
  const raw = storage.getItem(key(scope));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredP1InterruptionState>;
    if (
      parsed.version !== 1 ||
      parsed.tenantId !== scope.tenantId ||
      parsed.actorId !== scope.actorId ||
      !Array.isArray(parsed.handledItemIds) ||
      parsed.handledItemIds.some((id) => typeof id !== 'string')
    ) {
      storage.removeItem(key(scope));
      return null;
    }
    return parsed as StoredP1InterruptionState;
  } catch {
    storage.removeItem(key(scope));
    return null;
  }
}

export function hasHandledP1Interruption(
  scope: P1InterruptionScope,
  itemId: string,
  storage: Storage | undefined = storageOrUndefined()
): boolean {
  return readState(scope, storage)?.handledItemIds.includes(itemId) ?? false;
}

export function rememberP1Interruption(
  scope: P1InterruptionScope,
  itemId: string,
  storage: Storage | undefined = storageOrUndefined()
): void {
  if (!scope.tenantId || !scope.actorId || !itemId || !storage) return;
  const current = readState(scope, storage)?.handledItemIds ?? [];
  const handledItemIds = [itemId, ...current.filter((id) => id !== itemId)].slice(0, MAX_HANDLED);
  storage.setItem(
    key(scope),
    JSON.stringify({ version: 1, tenantId: scope.tenantId, actorId: scope.actorId, handledItemIds })
  );
}

export function p1InterruptionHref(
  decision: P1InterruptionDecision,
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
): string | null {
  const item = decision.item;
  if (!decision.policy.enabled || decision.policy.priority !== 'P1' || !item) return null;
  const destination = workbenchTakeLiveDestination({
    clientId: item.customerId,
    deviceId: item.deviceId,
    evidenceSource: item.sourceType,
    evidenceId: item.sourceId,
    intendedAction: item.actionType,
    returnTo: location,
  });
  if (destination.kind !== 'ready') return null;
  const url = new URL(destination.href, 'https://hyperion.local');
  url.searchParams.set('interruption', 'p1');
  url.searchParams.set('queueItem', item.id);
  return `${url.pathname}${url.search}`;
}
